import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import ManagerSidebar from "../components/Sidebar/ManagerSidebar";
import "./EditAttendance.css";

const API_BASE_URL =
  "https://ikaze-thouse-backend.vercel.app/api/v1";

const EditAttendance = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [guards, setGuards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  const [formData, setFormData] = useState({
    guard_id: "",
    date: "",
    shift_type: "DAY",
    check_in: "",
    check_out: "",
    late_minutes: 0,
    rating: 5
  });

  // ================= FORMAT LATE TIME =================
  const formatLateTime = (totalMinutes) => {
    if (totalMinutes <= 0) return "On Time";

    if (totalMinutes < 60) {
      return `${totalMinutes} mins`;
    }

    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;

    return mins > 0
      ? `${hours}h ${mins}m`
      : `${hours}h`;
  };

  // ================= AUTO CALCULATE =================
  useEffect(() => {
    if (!formData.check_in) return;

    const [hours, minutes] =
      formData.check_in.split(":").map(Number);

    const actualMinutes = hours * 60 + minutes;

    const expectedMinutes =
      formData.shift_type === "DAY"
        ? 360
        : 1080;

    let delay = actualMinutes - expectedMinutes;

    // Night shift after midnight
    if (
      formData.shift_type === "NIGHT" &&
      hours < 12
    ) {
      delay =
        actualMinutes + 1440 - expectedMinutes;
    }

    const lateMins = Math.max(0, delay);

    let autoRating = 5;

    if (lateMins === 0) autoRating = 5;
    else if (lateMins <= 15) autoRating = 4.5;
    else if (lateMins <= 30) autoRating = 4;
    else if (lateMins <= 60) autoRating = 3;
    else if (lateMins <= 120) autoRating = 2;
    else autoRating = 1;

    setFormData((prev) => ({
      ...prev,
      late_minutes: lateMins,
      rating: autoRating
    }));
  }, [formData.check_in, formData.shift_type]);

  // ================= LOAD DATA =================
  useEffect(() => {
    const loadData = async () => {
      try {
        const [attendanceRes, guardsRes] =
          await Promise.all([
            axios.get(
              `${API_BASE_URL}/attendance/${id}`
            ),
            axios.get(
              `${API_BASE_URL}/guards/`
            )
          ]);

        const data = attendanceRes.data;

        setFormData({
          guard_id: data.guard_id || "",
          date: data.date?.split("T")[0] || "",
          shift_type:
            data.shift_type || "DAY",

          check_in: data.check_in
            ? data.check_in.substring(11, 16)
            : "",

          check_out: data.check_out
            ? data.check_out.substring(11, 16)
            : "",

          late_minutes:
            data.late_minutes || 0,

          rating: data.rating || 5
        });

        const guardsData =
          guardsRes.data?.guards ||
          guardsRes.data ||
          [];

        setGuards(
          Array.isArray(guardsData)
            ? guardsData
            : []
        );
      } catch (error) {
        console.error("Load error:", error);
        alert(
          "Failed to load attendance data"
        );
      } finally {
        setFetching(false);
      }
    };

    loadData();
  }, [id]);

  // ================= UPDATE =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);

    try {
      const payload = {
        guard_id: formData.guard_id,
        date: formData.date,
        shift_type: formData.shift_type,

        check_in: formData.check_in
          ? `${formData.date}T${formData.check_in}:00Z`
          : null,

        check_out: formData.check_out
          ? `${formData.date}T${formData.check_out}:00Z`
          : null,

        late_minutes: Number(
          formData.late_minutes
        ),

        rating: Number(formData.rating)
      };

      await axios.put(
        `${API_BASE_URL}/attendance/${id}`,
        payload
      );

      alert(
        "Attendance updated successfully"
      );

      navigate("/report_guards");
    } catch (err) {
      console.error("Update error:", err);

      alert("Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="loading">
        Loading attendance...
      </div>
    );
  }

  return (
    <div className="edit-wrapper">
      <ManagerSidebar />

      <div className="edit-container">
        <form
          className="edit-card"
          onSubmit={handleSubmit}
        >
          <h2>✏️ Edit Attendance</h2>

          {/* GUARD */}
          <label>Guard</label>

          <select
            value={formData.guard_id}
            onChange={(e) =>
              setFormData({
                ...formData,
                guard_id: e.target.value
              })
            }
          >
            <option value="">
              Select Guard
            </option>

            {guards.map((g) => (
              <option
                key={g.id}
                value={g.id}
              >
                {g.name}
              </option>
            ))}
          </select>

          {/* DATE */}
          <label>Date</label>

          <input
            type="date"
            value={formData.date}
            onChange={(e) =>
              setFormData({
                ...formData,
                date: e.target.value
              })
            }
          />

          {/* SHIFT */}
          <label>Shift</label>

          <select
            value={formData.shift_type}
            onChange={(e) =>
              setFormData({
                ...formData,
                shift_type: e.target.value
              })
            }
          >
            <option value="DAY">
              Day Shift
            </option>

            <option value="NIGHT">
              Night Shift
            </option>
          </select>

          {/* CHECK IN / OUT */}
          <div className="row">
            <div>
              <label>Check In</label>

              <input
                type="time"
                value={formData.check_in}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    check_in:
                      e.target.value
                  })
                }
              />
            </div>

            <div>
              <label>Check Out</label>

              <input
                type="time"
                value={formData.check_out}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    check_out:
                      e.target.value
                  })
                }
              />
            </div>
          </div>

          {/* LATE */}
          <label>Lateness Duration</label>

          <input
            type="text"
            readOnly
            value={formatLateTime(
              formData.late_minutes
            )}
          />

          {/* RATING */}
          <label>Performance Rating</label>

          <input
            type="text"
            readOnly
            value={formData.rating}
          />

          <button
            type="submit"
            disabled={loading}
          >
            {loading
              ? "Updating..."
              : "Update Attendance"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default EditAttendance;